interface Url<T> {
    hostname: string;
    pathname?: string;
    protocol: T;
}

type HTTPSchema = 'http' | 'https';
type WebsocketSchema = 'ws' | 'wss';

interface WebsocketConnection extends Url<WebsocketSchema> {
    url: string;
}

interface HTTPConnection extends Url<HTTPSchema> {
    url: string;
    proxyPath?: string;
}

export interface WebsocketConnections {
    api: HTTPConnection;
    yacs: WebsocketConnection;
}

export interface APIConnections {
    yat: HTTPConnection;
    yatNode: HTTPConnection;
    api: HTTPConnection;
    daas: HTTPConnection;
    daasV2: HTTPConnection;
    daasGraphQL: HTTPConnection;
    gs: HTTPConnection;
    auth: HTTPConnection;
    marketplace: HTTPConnection;
}

function getUrl(url: Url<HTTPSchema | WebsocketSchema>) {
    const assembledUrl =
        url.pathname && url.pathname !== '/'
            ? `${url.protocol}://${url.hostname}${url.pathname}`
            : `${url.protocol}://${url.hostname}`;
    const newUrl = new URL(assembledUrl);
    const href = newUrl.href;
    if (href.endsWith('/')) {
        return href.slice(0, -1);
    }
    return href;
}
// Shallow clone to fix issues with empty values
const env = Object.assign({}, process.env);

const yatApiUrl: Url<HTTPSchema> = {
    hostname: env.REACT_APP_API_DOMAIN || 'y.at/a',
    protocol: (env.REACT_APP_API_PREFIX as HTTPSchema) || 'https',
};

const yatApiNodeUrl: Url<HTTPSchema> = {
    hostname: env.REACT_APP_NODE_API_DOMAIN || 'y.at/nbe',
    protocol: (env.REACT_APP_NODE_API_PREFIX as HTTPSchema) || 'https',
};

const wsApiUrl: Url<HTTPSchema> = {
    hostname: env.REACT_APP_WS_DOMAIN || env.REACT_APP_API_DOMAIN || 'y.at',
    protocol: (env.REACT_APP_WS_PROTOCOL as HTTPSchema) || 'wss',
};

const gsApiUrl: Url<HTTPSchema> = {
    hostname: env.REACT_APP_GS_HOSTNAME || 'y.at',
    pathname: env.REACT_APP_GS_PATHNAME || '/s',
    protocol: (env.REACT_APP_API_PREFIX as HTTPSchema) || 'https',
};

const wsYacsUrl: Url<WebsocketSchema> = {
    hostname: env.REACT_APP_YACS_WS_HOSTNAME || 'y.y.at',
    pathname: env.REACT_APP_YACS_WS_PATHNAME || '/websocket',
    protocol: (env.REACT_APP_YACS_WS_PROTOCOL as WebsocketSchema) || 'wss',
};

const daasUrl: Url<HTTPSchema> = {
    hostname: env.REACT_APP_DAAS_HOSTNAME || 'y.y.at',
    pathname: env.REACT_APP_DAAS_PATHNAME || '/api/proxy',
    protocol: (env.REACT_APP_DAAS_PROTOCOL as HTTPSchema) || 'https',
};
const daasV2Url: Url<HTTPSchema> = {
    hostname: env.REACT_APP_DAAS_V2_HOSTNAME || 'daas.y.at',
    pathname: env.REACT_APP_DAAS_V2_PATHNAME || '/v2',
    protocol: (env.REACT_APP_DAAS_V2_PROTOCOL as HTTPSchema) || 'https',
};

const daasGraphQLUrl: Url<HTTPSchema> = {
    hostname: env.REACT_APP_DAAS_GRAPHQL_HOSTNAME || 'daas-gql.yat.fyi',
    pathname: env.REACT_APP_DAAS_GRAPHQL_PATHNAME || '/api/rest/',
    protocol: (env.REACT_APP_DAAS_GRAPHQL_PROTOCOL as HTTPSchema) || 'https',
};

const authUrl: Url<HTTPSchema> = {
    hostname: env.REACT_APP_AUTH_HOSTNAME || 'micro.yat.fyi',
    pathname: env.REACT_APP_AUTH_PATHNAME || '/auth',
    protocol: (env.REACT_APP_AUTH_PROTOCOL as HTTPSchema) || 'https',
};

const marketplaceUrl: Url<HTTPSchema> = {
    hostname: env.REACT_APP_MARKETPLACE_GRAPHQL_HOSTNAME || 'micro.yat.fyi',
    pathname: env.REACT_APP_MARKETPLACE_GRAPHQL_PATHNAME || '/gql',
    protocol: (env.REACT_APP_MARKETPLACE_GRAPHQL_PROTOCOL as HTTPSchema) || 'https',
};

export const apiConnections: APIConnections = {
    yat: { ...yatApiUrl, url: getUrl(yatApiUrl) },
    yatNode: { ...yatApiNodeUrl, url: getUrl(yatApiNodeUrl), proxyPath: env.REACT_APP_NBE_PROXY_PATH },
    api: { ...wsApiUrl, url: getUrl(wsApiUrl) },
    daas: { ...daasUrl, url: getUrl(daasUrl), proxyPath: env.REACT_APP_DAAS_PROXY_PATH },
    daasV2: { ...daasV2Url, url: getUrl(daasV2Url), proxyPath: env.REACT_APP_DAAS_V2_PROXY_PATH },
    daasGraphQL: { ...daasGraphQLUrl, url: getUrl(daasGraphQLUrl) },
    gs: { ...gsApiUrl, url: getUrl(gsApiUrl), proxyPath: env.REACT_APP_GS_PROXY_PATH },
    auth: { ...authUrl, url: getUrl(authUrl), proxyPath: env.REACT_APP_AUTH_PROXY_PATH },
    marketplace: {
        ...marketplaceUrl,
        url: getUrl(marketplaceUrl),
        proxyPath: env.REACT_APP_MARKETPLACE_GRAPHQL_PROXY_PATH,
    },
};

export const websocketConnections: WebsocketConnections = {
    api: { ...wsApiUrl, url: getUrl(wsApiUrl) },
    yacs: { ...wsYacsUrl, url: getUrl({ ...wsYacsUrl, pathname: '/' }) },
};
